// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';

const NAV_ITEMS = {
  owner: [
    { section: 'Command' },
    { path: '/overwatch', icon: '\uD83D\uDD2D', label: 'Overwatch' },
    { path: '/dashboard', icon: '\uD83D\uDCCA', label: 'Dashboard' },
    { section: 'Products' },
    { path: '/product-catalog', icon: '\uD83D\uDED2', label: 'Product Catalog' },
    { path: '/inventory', icon: '\uD83D\uDCE6', label: 'Inventory' },
    { path: '/pricing', icon: '\uD83C\uDFF7\uFE0F', label: 'Pricing & Markdowns' },
    { section: 'Departments' },
    { path: '/departments', icon: '\uD83C\uDFEA', label: 'All Departments' },
    { path: '/produce', icon: '\uD83E\uDD66', label: 'Produce' },
    { path: '/deli-counter', icon: '\uD83E\uDD69', label: 'Deli Counter' },
    { path: '/bakery', icon: '\uD83C\uDF5E', label: 'Bakery' },
    { path: '/meat-seafood', icon: '\uD83E\uDD53', label: 'Meat & Seafood' },
    { section: 'Supply Chain' },
    { path: '/suppliers', icon: '\uD83D\uDE9B', label: 'Suppliers' },
    { path: '/purchase-orders', icon: '\uD83D\uDCC4', label: 'Purchase Orders' },
    { path: '/receiving', icon: '\uD83D\uDCE5', label: 'Receiving Dock' },
    { section: 'Janitorial & Compliance' },
    { path: '/janitorial', icon: '\uD83E\uDDF9', label: 'Janitorial Hub' },
    { path: '/cleaning-checklist', icon: '\u2705', label: 'Cleaning Checklists' },
    { path: '/incident-log', icon: '\u26A0\uFE0F', label: 'Incident Log' },
    { path: '/inspection-reports', icon: '\uD83D\uDD0D', label: 'Inspections' },
    { path: '/supply-inventory', icon: '\uD83E\uDDF4', label: 'Cleaning Supplies' },
    { section: 'Staff' },
    { path: '/staff-scheduling', icon: '\uD83D\uDCC5', label: 'Scheduling' },
    { path: '/training', icon: '\uD83C\uDF93', label: 'Training' },
    { path: '/payroll', icon: '\uD83D\uDCB0', label: 'Payroll' },
    { section: 'Financial' },
    { path: '/pos-overview', icon: '\uD83D\uDCB3', label: 'POS Overview' },
    { path: '/daily-sales', icon: '\uD83D\uDCC8', label: 'Daily Sales' },
    { path: '/profit-loss', icon: '\uD83D\uDCC9', label: 'Profit & Loss' },
    { path: '/shrinkage', icon: '\uD83D\uDEA8', label: 'Shrinkage & Loss' },
    { section: 'Storefront' },
    { path: '/online-store', icon: '\uD83D\uDECD\uFE0F', label: 'Online Store' },
    { path: '/coupons', icon: '\uD83C\uDF9F\uFE0F', label: 'Coupons' },
    { path: '/loyalty-program', icon: '\u2B50', label: 'Loyalty Program' },
    { path: '/delivery-pickup', icon: '\uD83D\uDE97', label: 'Delivery & Pickup' },
    { path: '/weekly-deals', icon: '\uD83D\uDCF0', label: 'Weekly Deals' },
    { path: '/shopping-list', icon: '\uD83D\uDCDD', label: 'Shopping Lists' },
    { path: '/order-history', icon: '\uD83D\uDCCB', label: 'Order History' },
    { section: 'Growth' },
    { path: '/marketing', icon: '\uD83D\uDCE2', label: 'Marketing' },
    { path: '/reviews', icon: '\u2B50', label: 'Reviews' },
    { section: 'Account' },
    { path: '/profile', icon: '\u2699\uFE0F', label: 'Settings' },
  ],
  manager: [
    { path: '/dashboard', icon: '\uD83D\uDCCA', label: 'Dashboard' },
    { section: 'Products' },
    { path: '/product-catalog', icon: '\uD83D\uDED2', label: 'Product Catalog' },
    { path: '/inventory', icon: '\uD83D\uDCE6', label: 'Inventory' },
    { path: '/pricing', icon: '\uD83C\uDFF7\uFE0F', label: 'Pricing' },
    { section: 'Departments' },
    { path: '/departments', icon: '\uD83C\uDFEA', label: 'Departments' },
    { path: '/produce', icon: '\uD83E\uDD66', label: 'Produce' },
    { path: '/deli-counter', icon: '\uD83E\uDD69', label: 'Deli' },
    { path: '/bakery', icon: '\uD83C\uDF5E', label: 'Bakery' },
    { path: '/meat-seafood', icon: '\uD83E\uDD53', label: 'Meat & Seafood' },
    { section: 'Supply Chain' },
    { path: '/suppliers', icon: '\uD83D\uDE9B', label: 'Suppliers' },
    { path: '/purchase-orders', icon: '\uD83D\uDCC4', label: 'Purchase Orders' },
    { path: '/receiving', icon: '\uD83D\uDCE5', label: 'Receiving' },
    { section: 'Janitorial' },
    { path: '/janitorial', icon: '\uD83E\uDDF9', label: 'Janitorial' },
    { path: '/cleaning-checklist', icon: '\u2705', label: 'Checklists' },
    { path: '/incident-log', icon: '\u26A0\uFE0F', label: 'Incidents' },
    { path: '/inspection-reports', icon: '\uD83D\uDD0D', label: 'Inspections' },
    { section: 'Staff' },
    { path: '/staff-scheduling', icon: '\uD83D\uDCC5', label: 'Scheduling' },
    { path: '/training', icon: '\uD83C\uDF93', label: 'Training' },
    { section: 'Financial' },
    { path: '/pos-overview', icon: '\uD83D\uDCB3', label: 'POS' },
    { path: '/daily-sales', icon: '\uD83D\uDCC8', label: 'Daily Sales' },
    { path: '/shrinkage', icon: '\uD83D\uDEA8', label: 'Shrinkage' },
    { section: 'Storefront' },
    { path: '/online-store', icon: '\uD83D\uDECD\uFE0F', label: 'Online Store' },
    { path: '/coupons', icon: '\uD83C\uDF9F\uFE0F', label: 'Coupons' },
    { path: '/weekly-deals', icon: '\uD83D\uDCF0', label: 'Weekly Deals' },
    { path: '/reviews', icon: '\u2B50', label: 'Reviews' },
    { path: '/profile', icon: '\u2699\uFE0F', label: 'Settings' },
  ],
  associate: [
    { path: '/dashboard', icon: '\uD83D\uDCCA', label: 'Dashboard' },
    { section: 'My Work' },
    { path: '/inventory', icon: '\uD83D\uDCE6', label: 'Inventory' },
    { path: '/receiving', icon: '\uD83D\uDCE5', label: 'Receiving' },
    { path: '/cleaning-checklist', icon: '\u2705', label: 'Cleaning' },
    { path: '/incident-log', icon: '\u26A0\uFE0F', label: 'Report Incident' },
    { path: '/staff-scheduling', icon: '\uD83D\uDCC5', label: 'My Schedule' },
    { path: '/training', icon: '\uD83C\uDF93', label: 'Training' },
    { path: '/profile', icon: '\u2699\uFE0F', label: 'Profile' },
  ],
  demo: [
    { section: 'Overview' },
    { path: '/dashboard', icon: '\uD83D\uDCCA', label: 'Dashboard' },
    { path: '/product-catalog', icon: '\uD83D\uDED2', label: 'Products' },
    { path: '/inventory', icon: '\uD83D\uDCE6', label: 'Inventory' },
    { path: '/departments', icon: '\uD83C\uDFEA', label: 'Departments' },
    { path: '/janitorial', icon: '\uD83E\uDDF9', label: 'Janitorial' },
    { path: '/online-store', icon: '\uD83D\uDECD\uFE0F', label: 'Online Store' },
    { path: '/coupons', icon: '\uD83C\uDF9F\uFE0F', label: 'Coupons' },
    { path: '/loyalty-program', icon: '\u2B50', label: 'Loyalty' },
    { path: '/daily-sales', icon: '\uD83D\uDCC8', label: 'Sales' },
    { path: '/profile', icon: '\u2699\uFE0F', label: 'Settings' },
  ],
};

const ADMIN_TEAM = [
  'J.lutz@miskulintechnologies.com', 'R.miskulin@miskulintechnologies.com', 'rmiskulin@gmail.com',
  'threewayindustries@gmail.com', 'desbachynski@gmail.com', 'lucas.s.mason@gmail.com',
  'leemehling@hotmail.com', 'cloricer77@gmail.com', 'tyson@canstorr.com',
  'tyler.hodgson32@gmail.com', 'jhansen860@yahoo.com', 'littlesderek@gmail.com',
  'laken.weston@live.ca', 'che@rosebudcannabisfarms.ca', 'shanon@lanewaymedia.ca',
  'amandakristaleigh@hotmail.com', 'dblahut@sastel.net', 'demo@iqapps.dev',
];

const OVERWATCH_TEAM = ADMIN_TEAM;

function getEffectiveRole(user) {
  if (!user) return 'associate';
  if (ADMIN_TEAM.map(e => e.toLowerCase()).includes((user.email || '').toLowerCase())) return 'owner';
  if (user.email === 'demo@iqapps.dev') return 'demo';
  return user.role || 'associate';
}

export default function Layout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [viewAsRole, setViewAsRole] = useState(null);
  const actualRole = getEffectiveRole(user);
  const role = viewAsRole || actualRole;
  let navItems = NAV_ITEMS[role] || NAV_ITEMS.owner;
  if (!OVERWATCH_TEAM.map(e => e.toLowerCase()).includes((user?.email || '').toLowerCase())) {
    navItems = navItems.filter(item => item.path !== '/overwatch');
  }
  const initials = user?.full_name?.split(' ').map(n => n[0]).join('') || 'U';

  React.useEffect(() => { setSidebarOpen(false); }, [location.pathname]);
  React.useEffect(() => {
    if (sidebarOpen) document.body.classList.add('sidebar-open');
    else document.body.classList.remove('sidebar-open');
    return () => document.body.classList.remove('sidebar-open');
  }, [sidebarOpen]);

  return (
    <div className="app-layout">
      <button className="mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? '\u2715' : '\u2630'}
      </button>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">AisleSync</div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Grocery & Retail</div>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item, i) =>
            item.section ? (
              <div key={i} className="nav-section">{item.section}</div>
            ) : (
              <NavLink key={item.path} to={item.path} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">{item.icon}</span>
                {item.label}
              </NavLink>
            )
          )}
          {actualRole === 'owner' && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: '#94a3b8', marginBottom: 8 }}>View As Role</div>
              {Object.keys(NAV_ITEMS).filter(r => r !== 'owner' && r !== 'demo').map(r => (
                <button key={r} onClick={() => setViewAsRole(viewAsRole === r ? null : r)}
                  style={{ display: 'block', width: '100%', padding: '6px 10px', marginBottom: 4, background: viewAsRole === r ? '#4F46E5' : 'rgba(255,255,255,0.05)', color: viewAsRole === r ? '#fff' : '#94a3b8', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', textAlign: 'left', textTransform: 'capitalize' }}>
                  {viewAsRole === r ? '\u2713 ' : ''}{r.replace(/_/g, ' ')}
                </button>
              ))}
              {viewAsRole && (
                <button onClick={() => setViewAsRole(null)}
                  style={{ display: 'block', width: '100%', padding: '6px 10px', marginTop: 4, background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: 'none', borderRadius: 6, fontSize: 11, cursor: 'pointer', textAlign: 'center', fontWeight: 700 }}>
                  Reset to Owner
                </button>
              )}
            </div>
          )}
        </nav>
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{initials}</div>
            <div>
              <div className="user-name">{user?.full_name || user?.email}</div>
              <div className="user-role">{viewAsRole ? `Viewing as: ${viewAsRole.replace(/_/g, ' ')}` : role.replace(/_/g, ' ')}</div>
            </div>
          </div>
          <button className="btn-logout" onClick={() => { signOut(); navigate('/login'); }}>Logout</button>
        </div>
      </aside>
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
